import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api";
import { AxiosError } from "axios";
import { OrgKey, UIThread } from "../../generated/core-api";
import { useMemo, useCallback } from "react";

export function useThread(workspaceId: string, threadId: string) {
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => ["workspace", workspaceId, "thread", threadId],
    [workspaceId, threadId]
  );

  const setThreadKnowledge = useCallback(
    async (knowledgeFromOrgs: OrgKey[]) => {
      try {
        // Update on the server
        await api.setThreadKnowledge(workspaceId, threadId, {
          knowledge_from_orgs: knowledgeFromOrgs,
        });

        // Update the client state immediately
        queryClient.setQueryData<UIThread>(queryKey, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            knowledge_from_orgs: knowledgeFromOrgs,
          };
        });
      } catch (error) {
        console.error("Failed to update thread knowledge:", error);
        throw error;
      }
    },
    [workspaceId, threadId, queryClient, queryKey]
  );

  const setThreadTitle = useCallback(
    async (title: string) => {
      try {
        await api.setThreadTitle(workspaceId, threadId, {
          title,
        });

        queryClient.invalidateQueries({
          queryKey: ["workspace", workspaceId, "thread"],
        });
      } catch (error) {
        console.error("Failed to update thread title:", error);
        throw error;
      }
    },
    [workspaceId, threadId, queryClient]
  );

  const inferThreadTitle = useCallback(async () => {
    try {
      await api.inferThreadTitle(workspaceId, threadId);

      queryClient.invalidateQueries({
        queryKey: ["workspace", workspaceId, "thread"],
      });
    } catch (error) {
      console.error("Failed to infer thread title:", error);
      throw error;
    }
  }, [workspaceId, threadId, queryClient]);

  const query = useQuery<UIThread, AxiosError>({
    queryKey,
    queryFn: () => api.getThread(workspaceId, threadId),
    // Thread cache is always updated explicitly via setQueryData (messages,
    // knowledge) or invalidateQueries (title), so we never need stale-based
    // background refetches. This also prevents a redundant getThread call
    // immediately after useCreateThread seeds the cache from its POST response.
    staleTime: Infinity,
  });

  return {
    ...query,
    setThreadKnowledge,
    setThreadTitle,
    inferThreadTitle,
  };
}
